/**
 * Leave approval queue — owner/admin/manager approve/deny staff leave
 * applications (Prompt 11E.6). Mirrors
 * @features/requests/pages/ApprovalQueuePage.tsx's shape, but leave
 * decisions are NOT scoped like the 11E.5 requests queue — a 'staff'
 * (teacher) caller has no access to this page at all (route-gated to
 * ADMIN_ROLES) since leave approval isn't an "own assigned students" kind
 * of authority (backend/app/features/leaves/router.py::_ADMIN_ROLES).
 */

import React, { useState } from 'react';
import {
  useApproveLeave,
  useDenyLeave,
  useLeaveQueue,
  type LeaveResponse,
  type LeaveStatus,
} from '@features/attendance/api/leaves';

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5rem',
  padding: '1rem 1.25rem',
  marginBottom: '0.75rem',
};

const statusColor: Record<LeaveStatus, string> = {
  pending: '#b45309',
  approved: '#15803d',
  denied: '#b91c1c',
};

const LeaveRow: React.FC<{ leave: LeaveResponse }> = ({ leave }) => {
  const [note, setNote] = useState('');
  const approve = useApproveLeave();
  const deny = useDenyLeave();
  const isPending = leave.status === 'pending';
  const busy = approve.isPending || deny.isPending;

  return (
    <div style={card} data-testid={`candeza-leave-queue-row-${leave.id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <strong style={{ textTransform: 'capitalize' }}>{leave.leave_type} leave</strong>
          <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
            Staff: {leave.staff_id} · {leave.start_date} → {leave.end_date} · {leave.days} day
            {leave.days === 1 ? '' : 's'}
            {leave.is_backdated_entry && ' · backdated entry'}
          </div>
          {leave.note && (
            <div style={{ fontSize: '0.8125rem', color: '#374151', marginTop: 4 }}>
              &ldquo;{leave.note}&rdquo;
            </div>
          )}
        </div>
        <span style={{ fontWeight: 600, textTransform: 'capitalize', color: statusColor[leave.status] }}>
          {leave.status}
        </span>
      </div>

      {isPending && (
        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            placeholder="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid={`candeza-leave-queue-note-${leave.id}`}
            style={{ flex: 1 }}
          />
          <button
            disabled={busy}
            onClick={() => approve.mutate({ id: leave.id, body: { note: note || undefined } })}
            data-testid={`candeza-leave-queue-approve-${leave.id}`}
          >
            Approve
          </button>
          <button
            disabled={busy}
            onClick={() => deny.mutate({ id: leave.id, body: { note: note || undefined } })}
            data-testid={`candeza-leave-queue-deny-${leave.id}`}
          >
            Deny
          </button>
        </div>
      )}
    </div>
  );
};

export const LeaveApprovalQueuePage: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<LeaveStatus | ''>('pending');
  const { data, isLoading, isError } = useLeaveQueue(statusFilter);
  const leaves = data?.items ?? [];

  return (
    <div data-testid="candeza-leave-queue-page">
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem' }}>
        Leaves — Approval Queue
      </h1>

      <div style={{ marginBottom: '1rem' }}>
        {(['pending', 'approved', 'denied', ''] as const).map((s) => (
          <button
            key={s || 'all'}
            onClick={() => setStatusFilter(s)}
            data-testid={`candeza-leave-queue-filter-${s || 'all'}`}
            style={{
              marginRight: '0.5rem',
              fontWeight: statusFilter === s ? 700 : 400,
            }}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div data-testid="candeza-leave-queue-loading">Loading…</div>
      ) : isError ? (
        <div data-testid="candeza-leave-queue-error" style={{ color: '#ef4444' }}>
          Could not load the leave approval queue.
        </div>
      ) : leaves.length === 0 ? (
        <p data-testid="candeza-leave-queue-empty" style={{ color: '#6b7280' }}>
          No leave applications here.
        </p>
      ) : (
        <div data-testid="candeza-leave-queue-list">
          {leaves.map((leave) => (
            <LeaveRow key={leave.id} leave={leave} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LeaveApprovalQueuePage;
